import type { PatternsResponse } from '@lms/shared-types'
import { DetectionCard } from '../primitives/DetectionCard'
import type { DetectionSeverity } from '../primitives/DetectionCard'
import type { SeverityKind } from '../primitives/Severity'
import { flags } from '../../lib/feature-flags'
import { ConfigurePatternLink } from '../hr/ConfigurePatternLink'

// ── Types ─────────────────────────────────────────────────────────────────────

type BasePatternKind = PatternsResponse['patterns'][number]['kind']

/**
 * Extended pattern kinds — supersets the live analysis enum with kinds that
 * only surface through the cached team-patterns endpoint or through scalar
 * approval-flag metrics in the Command Palette (`frequent_emergency`).
 * Renderers gate on PATTERN_META having the entry.
 */
export type PatternKind =
  | BasePatternKind
  | 'frequent_emergency'

type SeverityLevel = 'low' | 'medium' | 'high'

export interface PatternMeta {
  headline: string
  lane: 'emergency' | 'sick' | 'weekend'
}

export const PATTERN_META: Record<PatternKind, PatternMeta> = {
  frequent_sick_leave: {
    headline: 'Frequent sick leave',
    lane: 'sick',
  },
  poor_planning_emergency: {
    headline: 'Poor planning (emergency)',
    lane: 'emergency',
  },
  emergency_approaching_limit: {
    headline: 'Emergency approaching limit',
    lane: 'emergency',
  },
  unpaid_leave_taken: {
    headline: 'Unpaid leave taken',
    lane: 'sick',
  },
  frequent_half_days: {
    headline: 'Frequent half-days',
    lane: 'emergency',
  },
  long_weekend_pattern: {
    headline: 'Long-weekend pattern',
    lane: 'weekend',
  },
  no_leave_in_window: {
    headline: 'No leave in window',
    lane: 'sick',
  },
  high_early_utilisation: {
    headline: 'High early utilisation',
    lane: 'sick',
  },
  frequent_emergency: {
    headline: 'Frequent emergency leave',
    lane: 'emergency',
  },
}

/**
 * Structural input PatternCard renders. Wider than `PatternsResponse['patterns'][number]`
 * along the `kind` axis (accepts the extended PatternKind union) and narrower along
 * other axes (only `kind`, `severity`, `possibleSignal`, and `evidence.count` are read).
 * Extra fields (`recommendation`, `evidence.windowDays`, `evidence.sampleLeaveIds`) are
 * declared optional so live-analysis rows from `PatternsResponse` still satisfy
 * the type, while palette widgets can synthesize a pattern from a scalar metric
 * (e.g. `approval.leave_pattern_flags`) without forging a fake UUID list.
 *
 * `severity` is authoritative from the wire (set by HR via the pattern catalog).
 * Do NOT compute severity locally from evidence.count — HR catalog edits would not
 * propagate.
 */
export interface PatternCardInput {
  kind: PatternKind
  severity: SeverityLevel
  possibleSignal: string
  recommendation?: string
  evidence: {
    count: number
    windowDays?: number
    sampleLeaveIds?: ReadonlyArray<string>
  }
}

function severityToDetection(s: SeverityLevel): DetectionSeverity {
  if (s === 'high') return 'danger'
  if (s === 'medium') return 'warning'
  return 'info'
}

/** @public — used by EmployeePatternsModal */
export function severityToKind(s: SeverityLevel): SeverityKind {
  if (s === 'high') return 'high'
  if (s === 'medium') return 'medium'
  return 'info'
}

/** @public — used by EmployeePatternsModal */
export function severityColor(s: SeverityLevel): string {
  if (s === 'high') return 'var(--danger)'
  if (s === 'medium') return 'var(--warning)'
  return 'var(--success)'
}

function severityLabel(s: SeverityLevel, count: number): string {
  return `${count} ${count === 1 ? 'leave' : 'leaves'}`
}

// ── PatternCard ───────────────────────────────────────────────────────────────

interface PatternCardProps {
  pattern: PatternCardInput
  onOpenDetail: (pattern: PatternCardInput) => void
  onOpenDraft: (kind: string) => void
}

/**
 * Leave-pattern card — thin domain wrapper around DetectionCard.
 * Maps PATTERN_META + severity thresholds to DetectionCard props.
 * All leave-domain logic (PATTERN_META, SeverityLevel, bar soft-cap) stays here.
 */
/** Fallback meta for unknown/legacy pattern kinds received from the server. */
const UNKNOWN_META: PatternMeta = {
  headline: 'Leave pattern detected',
  lane: 'sick',
}

export function PatternCard({
  pattern,
  onOpenDetail,
  onOpenDraft,
}: PatternCardProps): JSX.Element {
  const meta = PATTERN_META[pattern.kind] ?? UNKNOWN_META
  const sev = pattern.severity
  const color = severityColor(sev)
  // Bar: map count to 0–100 with a soft cap of 6.
  const barPct = Math.min(100, Math.round((pattern.evidence.count / 6) * 100))

  const actions = (
    <>
      <ConfigurePatternLink code={pattern.kind} variant="text" />
      <button
        type="button"
        onClick={() => onOpenDetail(pattern)}
        style={{
          padding: '6px 12px',
          border: '1px solid var(--border)',
          borderRadius: 6,
          background: 'var(--surface)',
          color: 'var(--text-2)',
          fontWeight: 600,
          cursor: 'pointer',
          fontSize: 12,
          minHeight: 44,
        }}
      >
        Open detail
      </button>
      {flags.aiConversationDrafter && sev !== 'low' ? (
        <button
          type="button"
          data-testid="open-conversation-drafter"
          onClick={() => onOpenDraft(pattern.kind)}
          style={{
            padding: '6px 12px',
            background: 'var(--steel-blue)',
            color: 'var(--surface)',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 600,
            minHeight: 44,
            whiteSpace: 'nowrap',
          }}
        >
          Draft 1:1
        </button>
      ) : null}
    </>
  )

  return (
    <li
      data-testid="pattern-card"
      data-kind={pattern.kind}
      data-severity={sev}
      data-lane={meta.lane}
    >
      <DetectionCard
        algo="Detected"
        severity={severityToDetection(sev)}
        severityLabel={severityLabel(sev, pattern.evidence.count)}
        headline={meta.headline}
        description={pattern.possibleSignal}
        bar={{ value: barPct, total: 100, color }}
        actions={actions}
      />
    </li>
  )
}
